import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchRecurringInvoices } from '../../lib/queryFunctions';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArrowLeft, Plus, Search, Play, Pause, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const fmt = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
};

const freqLabels = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annually: 'Semi-Annually',
  annually: 'Annually',
};

const RecurringInvoicesPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [running, setRunning] = useState(null);

  const { data: templates = [], isLoading: loading } = useQuery({
    queryKey: QK.recurringInvoices(),
    queryFn: fetchRecurringInvoices(),
    onError: () => toast.error('Failed to load templates'),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: QK.recurringInvoices() });

  const handleRun = async (e, id) => {
    e.stopPropagation();
    setRunning(id);
    try {
      const res = await api.post(`/recurring-invoices/${id}/run`);
      toast.success(`Invoice ${res.data.invoice_number} created`);
      invalidate();
    } catch { toast.error('Failed to generate invoice'); }
    setRunning(null);
  };

  const handleToggle = async (e, id) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/recurring-invoices/${id}/toggle`);
      toast.success(res.data.is_active ? 'Template activated' : 'Template paused');
      invalidate();
    } catch { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!await confirm({ title: 'Delete this recurring template?', destructive: true })) return;
    try {
      await api.delete(`/recurring-invoices/${id}`);
      toast.success('Template deleted');
      invalidate();
    } catch { toast.error('Failed to delete'); }
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setDialogOpen(true);
  };

  const openEdit = (template) => {
    setEditingTemplate(template);
    setDialogOpen(true);
  };

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')} data-testid="back-to-invoices-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Invoices
            </Button>
            <div>
              <h1 className="font-serif text-3xl font-semibold" data-testid="recurring-invoices-title">Recurring Invoices</h1>
              <p className="text-muted-foreground mt-1">Automate regular billing with recurring templates</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-primary hover:bg-primary/90 text-white" data-testid="create-recurring-btn">
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12">
                <RefreshCw className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                <p className="text-muted-foreground">No recurring templates yet</p>
                <p className="text-sm text-muted-foreground mt-1">Create a template to automate regular invoicing</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Account / Owner</TableHead>
                    <TableHead>Horse</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Next Run</TableHead>
                    <TableHead>Runs</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map(t => {
                    const totalAmount = (t.entries || []).reduce((s, e) => s + (e.price * e.units), 0);
                    return (
                      <TableRow key={t.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openEdit(t)} data-testid={`recurring-row-${t.id}`}>
                        <TableCell className="font-medium">{t.name}</TableCell>
                        <TableCell>{t.crm_entity_name || '-'}</TableCell>
                        <TableCell>{t.horse_name || '-'}</TableCell>
                        <TableCell>{freqLabels[t.frequency] || t.frequency}</TableCell>
                        <TableCell className="font-medium">{fmt(totalAmount)}</TableCell>
                        <TableCell className="text-sm">
                          {t.next_run_date ? new Date(t.next_run_date).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{t.run_count || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                            {t.is_active ? 'Active' : 'Paused'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {t.is_active && t.run_count > 0 ? (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Pause" onClick={(e) => handleToggle(e, t.id)} data-testid={`toggle-template-${t.id}`}>
                                <Pause className="w-3.5 h-3.5" />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title={t.run_count > 0 ? 'Resume' : 'Run now'} onClick={(e) => t.run_count > 0 ? handleToggle(e, t.id) : handleRun(e, t.id)} disabled={running === t.id} data-testid={`run-template-${t.id}`}>
                                {running === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" title="Delete" onClick={(e) => handleDelete(e, t.id)} data-testid={`delete-template-${t.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <TemplateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        template={editingTemplate}
        onSaved={() => { setDialogOpen(false); setEditingTemplate(null); invalidate(); }}
      />
    </SidebarLayout>
  );
};

const TemplateDialog = ({ open, onOpenChange, template, onSaved }) => {
  const isEdit = !!template;
  const [entities, setEntities] = useState([]);
  const [horses, setHorses] = useState([]);
  const [standardItems, setStandardItems] = useState([]);
  const [entitySearch, setEntitySearch] = useState('');
  const [saving, setSaving] = useState(false);

  const defaultForm = {
    name: '',
    crm_entity_id: '',
    horse_id: '',
    frequency: 'monthly',
    entries: [{ items: '', description: '', units: 1, price: 0 }],
    other_notes_terms: '',
    next_run_date: new Date().toISOString().split('T')[0],
  };

  const confirm = useConfirm();
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (open) {
      setEntitySearch('');
      if (template) {
        setForm({
          name: template.name || '',
          crm_entity_id: template.crm_entity_id || '',
          horse_id: template.horse_id || '',
          frequency: template.frequency || 'monthly',
          entries: (template.entries || []).map(e => ({
            id: e.id,
            items: e.items || '',
            description: e.description || '',
            units: e.units ?? 1,
            price: e.price ?? 0,
          })),
          other_notes_terms: template.other_notes_terms || '',
          next_run_date: template.next_run_date ? template.next_run_date.split('T')[0] : new Date().toISOString().split('T')[0],
        });
      } else {
        setForm({ ...defaultForm, next_run_date: new Date().toISOString().split('T')[0] });
      }
      Promise.all([
        api.get('/crm-entities'),
        api.get('/horses'),
        api.get('/invoices/standard-items'),
      ]).then(([e, h, s]) => {
        setEntities((e.data || []).filter(x => x.crm_class === 'account'));
        setHorses(h.data || []);
        setStandardItems(s.data || []);
      }).catch(() => {});
    }
  }, [open, template]);

  const addEntry = (item) => {
    if (item) {
      setForm(f => ({ ...f, entries: [...f.entries, { items: item.items, description: item.description || '', units: 1, price: item.price }] }));
    } else {
      setForm(f => ({ ...f, entries: [...f.entries, { items: '', description: '', units: 1, price: 0 }] }));
    }
  };

  const updateEntry = (idx, field, value) => {
    setForm(f => {
      const entries = [...f.entries];
      entries[idx] = { ...entries[idx], [field]: value };
      return { ...f, entries };
    });
  };

  const removeEntry = (idx) => {
    setForm(f => ({ ...f, entries: f.entries.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.crm_entity_id) { toast.error('Please select an account'); return; }
    if (!form.name.trim()) { toast.error('Please enter a template name'); return; }
    if (form.entries.length === 0) { toast.error('Please add at least one line item'); return; }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/recurring-invoices/${template.id}`, form);
        toast.success('Template updated');
      } else {
        await api.post('/recurring-invoices', form);
        toast.success('Template created');
      }
      onSaved();
    } catch { toast.error(isEdit ? 'Failed to update template' : 'Failed to create template'); }
    setSaving(false);
  };

  const filteredEntities = entitySearch.length > 0
    ? entities.filter(e => e.system_name?.toLowerCase().includes(entitySearch.toLowerCase())).slice(0, 8)
    : [];
  const selectedEntity = entities.find(e => e.id === form.crm_entity_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Template' : 'Create Recurring Invoice Template'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Template Name *</Label>
            <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Monthly Boarding - Horse Name" data-testid="template-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Account / Owner *</Label>
              {selectedEntity ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{selectedEntity.system_name}</Badge>
                  <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => setForm(f => ({ ...f, crm_entity_id: '' }))}>Change</Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                    <Input className="pl-8 h-8 text-sm" placeholder="Search accounts..." value={entitySearch} onChange={(e) => setEntitySearch(e.target.value)} data-testid="template-entity-search" />
                  </div>
                  {filteredEntities.length > 0 && (
                    <div className="border rounded max-h-24 overflow-y-auto">
                      {filteredEntities.map(e => (
                        <div key={e.id} className="p-1.5 text-xs hover:bg-muted/50 cursor-pointer" onClick={async () => { setForm(f => ({ ...f, crm_entity_id: e.id })); setEntitySearch(''); }}>
                          {e.system_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Horse</Label>
              <Select value={form.horse_id || ''} onValueChange={(v) => setForm(f => ({ ...f, horse_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select horse (optional)" /></SelectTrigger>
                <SelectContent>
                  {horses.map(h => <SelectItem key={h.id} value={h.id}>{h.system_name || h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Frequency *</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm(f => ({ ...f, frequency: v }))} data-testid="frequency-select">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="semi_annually">Semi-Annually</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isEdit ? 'Next Run Date' : 'First Run Date'}</Label>
              <Input type="date" className={form.next_run_date ? 'date-filled' : 'text-muted-foreground'} value={form.next_run_date} onChange={(e) => setForm(f => ({ ...f, next_run_date: e.target.value }))} data-testid="next-run-date-input" />
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Line Items</Label>
              <div className="flex gap-2">
                {standardItems.length > 0 && (
                  <Select onValueChange={(v) => { const item = standardItems.find(s => s.id === v); if (item) addEntry(item); }}>
                    <SelectTrigger className="h-7 text-xs w-40"><SelectValue placeholder="From template" /></SelectTrigger>
                    <SelectContent>
                      {standardItems.map(s => <SelectItem key={s.id} value={s.id}>{s.items} (${s.price})</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addEntry()}>
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {form.entries.map((entry, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3"><Input className="h-8 text-xs" placeholder="Item" value={entry.items} onChange={(e) => updateEntry(idx, 'items', e.target.value)} /></div>
                  <div className="col-span-4"><Input className="h-8 text-xs" placeholder="Description" value={entry.description} onChange={(e) => updateEntry(idx, 'description', e.target.value)} /></div>
                  <div className="col-span-2"><Input className="h-8 text-xs" type="number" placeholder="Units" value={entry.units} onChange={(e) => updateEntry(idx, 'units', parseFloat(e.target.value) || 1)} /></div>
                  <div className="col-span-2"><Input className="h-8 text-xs" type="number" placeholder="Price" value={entry.price} onChange={(e) => updateEntry(idx, 'price', parseFloat(e.target.value) || 0)} /></div>
                  <div className="col-span-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500" onClick={() => removeEntry(idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground text-right">
              Total per invoice: {fmt(form.entries.reduce((sum, e) => sum + (parseFloat(e.price) || 0) * (parseFloat(e.units) || 1), 0))}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Notes / Terms</Label>
            <Textarea rows={2} value={form.other_notes_terms} onChange={(e) => setForm(f => ({ ...f, other_notes_terms: e.target.value }))} placeholder="Additional notes..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white" data-testid="save-template-btn">
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecurringInvoicesPage;
